import {
    ProjectPlanOrderResponse,
    ProjectPlanResponse,
    ProjectPlanRowResponse,
    ProjectPlanSegmentResponse,
    ProjectPlanTermResponse,
} from "./project-plan-builder";

/**
 * Черновик передвижки сроков (этап 6 ТЗ «Графики»). Сервер хранит его как список
 * операций и сам считает результат (PlanDraftEngine) при применении; на странице тот же
 * расчёт повторяет plan-draft-local.ts. Здесь — наложение результата на график: новое
 * положение работ и прежнее бледным контуром.
 */

/** Виды операций — совпадают с PlanOperationKinds на бэке */
export type PlanOperationKind =
    | "work"
    | "work-start"
    | "work-end"
    | "section"
    | "section-start"
    | "section-end"
    | "project"
    | "project-end"
    | "milestone";

/** Операция, которую страница отправляет в черновик. Даты — yyyy-MM-dd */
export interface PlanOperationInput {
    kind: PlanOperationKind;
    assignmentId?: string | null;
    orderId?: string | null;
    contentGuid?: string | null;
    milestoneId?: string | null;
    /** Сдвиг в рабочих днях: плюс — вправо */
    shift?: number;
    /** Новая дата края для операций над сроком */
    day?: string | null;
    /** «Сдвинуть с даты»: двигается только то, что не раньше */
    fromDay?: string | null;
}

export interface PlanDraftOperation extends PlanOperationInput {
    index: number;
    /** Почему операция не применилась; null — применилась */
    rejected: string | null;
}

/** Отрезок подряд идущих рабочих дней */
export interface PlanDraftRange {
    from: string;
    to: string;
    workDays: number;
}

export interface PlanDraftWork {
    id: string;
    employerId: string;
    orderId: string;
    contentGuid: string | null;
    before: PlanDraftRange[];
    after: PlanDraftRange[];
    /** На сколько рабочих дней работу толкнул каскад по связям; 0 — не толкал */
    pushed?: number;
}

/** Толчок каскада по связям: работа или раздел, на сколько рабочих дней */
export interface PlanDraftPush {
    kind: "work" | "section";
    orderId: string;
    assignmentId: string | null;
    contentGuid: string | null;
    employerId: string | null;
    shift: number;
    /** Связь, которую толчок не смог соблюсти, — только у blocked */
    linkId?: string | null;
    /** ГИП проекта — кому уйдёт запрос на согласование (только у blocked) */
    gipName?: string | null;
}

export interface PlanDraftMilestone {
    id: string;
    orderId: string;
    before: string;
    after: string;
}

/** Срок проекта (contentGuid пуст) или раздела; before* пусты — срок не был задан */
export interface PlanDraftTerm {
    orderId: string;
    contentGuid: string | null;
    beforeStart: string | null;
    beforeEnd: string | null;
    afterStart: string | null;
    afterEnd: string | null;
}

export interface PlanDraftView {
    active: boolean;
    started: string | null;
    operations: PlanDraftOperation[];
    works: PlanDraftWork[];
    milestones: PlanDraftMilestone[];
    terms: PlanDraftTerm[];
    changesCount: number;
    orders: string[];
    /** Что каскад толкнул по связям */
    pushed?: PlanDraftPush[];
    /** Что толкнуть нужно, но нет прав: связь останется нарушенной до согласования */
    blocked?: PlanDraftPush[];
    /** Проекты, которые задел только каскад, — «Затронуто в других проектах» */
    cascadeOrders?: string[];
}

export interface PlanDraftDiscrepancy {
    orderId: string;
    title: string;
    modifiedBy: string;
    modified: string;
}

export interface PlanDraftApplyResult {
    applied: boolean;
    discrepancies: PlanDraftDiscrepancy[];
    rejected: PlanDraftOperation[];
    /** Проекты, чьим ГИПам ушли запросы на согласование: каскад упёрся в права */
    sentForApproval?: string[];
}

export const EMPTY_DRAFT: PlanDraftView = {
    active: false,
    started: null,
    operations: [],
    works: [],
    milestones: [],
    terms: [],
    changesCount: 0,
    orders: [],
    pushed: [],
    blocked: [],
    cascadeOrders: [],
};

/** Ключ срока: проект — без раздела */
export const termKey = (orderId: string, contentGuid: string | null | undefined) => `${orderId}/${contentGuid || ""}`;

/**
 * Наложить черновик на ответ графика: отрезки изменённых работ заменяются новыми,
 * прежние остаются бледным контуром (ghost), полосы проектов и разделов с такими
 * работами пересчитываются по видимым строкам, вехи и сроки встают на новые даты.
 * Ответ не мутируется — изменённые узлы копируются.
 */
export function applyDraft(response: ProjectPlanResponse, draft: PlanDraftView | null): ProjectPlanResponse {
    if (!draft || !draft.active || (!draft.works.length && !draft.milestones.length && !draft.terms.length)) {
        return response;
    }

    const works = new Map(draft.works.map((work) => [work.id, work]));
    const milestones = new Map(draft.milestones.map((milestone) => [milestone.id, milestone]));
    const touched = new Set(draft.orders);

    return {
        ...response,
        orders: (response.orders || []).map((order) =>
            touched.has(order.id) ? applyToOrder(order, draft, works, milestones) : order
        ),
    };
}

function applyToOrder(
    order: ProjectPlanOrderResponse,
    draft: PlanDraftView,
    works: Map<string, PlanDraftWork>,
    milestones: Map<string, PlanDraftMilestone>
): ProjectPlanOrderResponse {
    const orderWorks = draft.works.filter((work) => work.orderId === order.id);

    const rows = orderWorks.length
        ? (order.rows || []).map((row) => applyToRow(row, orderWorks, works, null))
        : order.rows;

    const sections = orderWorks.length
        ? (order.sections || []).map((section) => {
              const sectionWorks = orderWorks.filter((work) => (work.contentGuid || "") === (section.id || ""));

              if (!sectionWorks.length && !(section.rows || []).some((row) => hasWork(row, works))) {
                  return section;
              }

              const sectionRows = (section.rows || []).map((row) =>
                  applyToRow(row, sectionWorks, works, section.id || "")
              );

              return { ...section, rows: sectionRows, span: spanOf(sectionRows, section.span) };
          })
        : order.sections;

    const orderMilestones = (order.milestones || []).map((milestone) => {
        const changed = milestones.get(milestone.id);
        return changed ? { ...milestone, day: changed.after } : milestone;
    });

    const terms = applyTerms(order.id, order.terms || [], draft.terms);

    const span = orderWorks.length ? spanOf(rows || [], order.span) : order.span;

    return {
        ...order,
        rows,
        sections,
        milestones: orderMilestones,
        terms,
        span,
        lastDay: span && span.stopDay > (order.lastDay || "") ? span.stopDay : order.lastDay,
    };
}

function hasWork(row: ProjectPlanRowResponse, works: Map<string, PlanDraftWork>): boolean {
    return (row.segments || []).some((segment) => !!segment.assignmentId && works.has(segment.assignmentId));
}

/**
 * Строка участника: отрезки изменённых работ уходят, вместо них — новое положение
 * и контур прежнего. section — guid раздела строки в режиме «Разделы», null — режим
 * «Сотрудники»: там строка собирает все работы сотрудника по проекту.
 */
function applyToRow(
    row: ProjectPlanRowResponse,
    orderWorks: PlanDraftWork[],
    works: Map<string, PlanDraftWork>,
    section: string | null
): ProjectPlanRowResponse {
    if (row.kind !== "employer") {
        return row;
    }

    const own = orderWorks.filter(
        (work) => work.employerId === row.id && (section === null || (work.contentGuid || "") === section)
    );

    if (!own.length && !hasWork(row, works)) {
        return row;
    }

    const kept = (row.segments || []).filter((segment) => !segment.assignmentId || !works.has(segment.assignmentId));

    const moved: ProjectPlanSegmentResponse[] = [];

    own.forEach((work) => {
        work.before.forEach((range) => moved.push(toSegment(range, work.id, true)));
        work.after.forEach((range) => moved.push(toSegment(range, work.id, false)));
    });

    return {
        ...row,
        segments: [...kept, ...moved].sort((left, right) =>
            left.from < right.from ? -1 : left.from > right.from ? 1 : 0
        ),
    };
}

function toSegment(range: PlanDraftRange, assignmentId: string, ghost: boolean): ProjectPlanSegmentResponse {
    return {
        from: range.from,
        to: range.to,
        stopDay: range.to,
        workDays: range.workDays,
        continuesLeft: false,
        continuesRight: false,
        assignmentId,
        ghost,
    };
}

/**
 * Полоса по отрезкам строк после черновика. Края, которые не сдвинулись, наследуют
 * признак «уходит за экран» у серверной полосы: она считалась по всем дням, в том
 * числе за пределами загруженного диапазона.
 */
function spanOf(
    rows: ProjectPlanRowResponse[],
    original: ProjectPlanSegmentResponse | null
): ProjectPlanSegmentResponse | null {
    const segments = rows.flatMap((row) => (row.segments || []).filter((segment) => !segment.ghost));

    if (!segments.length) {
        return null;
    }

    const from = segments.reduce(
        (result, segment) => (segment.from < result ? segment.from : result),
        segments[0].from
    );
    const to = segments.reduce((result, segment) => (segment.to > result ? segment.to : result), segments[0].to);
    const stopDay = segments.reduce(
        (result, segment) => (segment.stopDay > result ? segment.stopDay : result),
        segments[0].stopDay || to
    );

    return {
        from,
        to,
        stopDay,
        workDays: original ? original.workDays : 0,
        continuesLeft: !!original && original.continuesLeft && original.from === from,
        continuesRight: !!original && original.continuesRight && original.to === to,
        assignmentId: null,
        ghost: false,
    };
}

function applyTerms(
    orderId: string,
    terms: ProjectPlanTermResponse[],
    changes: PlanDraftTerm[]
): ProjectPlanTermResponse[] {
    const own = changes.filter((change) => change.orderId === orderId);

    if (!own.length) {
        return terms;
    }

    const result = terms.map((term) => {
        const change = own.find((item) => (item.contentGuid || "") === (term.contentGuid || ""));
        return change ? { contentGuid: term.contentGuid, start: change.afterStart, end: change.afterEnd } : term;
    });

    own.forEach((change) => {
        if (!terms.some((term) => (term.contentGuid || "") === (change.contentGuid || ""))) {
            result.push({ contentGuid: change.contentGuid, start: change.afterStart, end: change.afterEnd });
        }
    });

    return result;
}
